import { useState, useEffect } from "react";
import { Menu, MenuItem, SubMenu, ProSidebar } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Box, Typography, Divider, IconButton, useMediaQuery } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import styled from "styled-components";
import DashboardIcon from '../assets/Images/DashboardIcon.svg';
import Reports from '../assets/Images/ReportsIcon.svg';
import VisualReportsIcon from '../assets/Images/VisualReportsIcon.svg';
import DataReportsIcon from '../assets/Images/DataReports.svg';
import ContentLibraryIcon from '../assets/Images/ContentLibraryIcon.svg';
import PromptsIcon from '../assets/Images/PromptsIcon.svg';
import SelectedDashboardIcon from '../assets/Images/SelectedDashboardIcon.svg';
import SelectedVisualReportsIcon from '../assets/Images/SelectedVisualReportsIcon.svg';
import SelectedDataReportsIcon from '../assets/Images/SelectedDataReportsIcon.svg';
import SelectedContentLibraryIcon from '../assets/Images/SelectedContentLibraryIcon.svg';
import SelectedPromptIcon from '../assets/Images/SelectedPromptsIcon.svg';
import MITLOGO from '../assets/Images/MIT_logosvg.svg';
import './Sidebar.css'


const Container = styled.div`
  height: 100vh;
`;

const iconMappings = {
  "Dashboard": {
    default: DashboardIcon,
    selected: SelectedDashboardIcon,
  },
  "Reports": {
    default: Reports,
    selected: Reports,
  },
  "Visual Reports": {
    default: VisualReportsIcon,
    selected: SelectedVisualReportsIcon,
  },
  "Data Reports": {
    default: DataReportsIcon,
    selected: SelectedDataReportsIcon,
  },
  "Content Library": {
    default: ContentLibraryIcon,
    selected: SelectedContentLibraryIcon,
  },
  "Prompts": {
    default: PromptsIcon,
    selected: SelectedPromptIcon,
  },
  "Winning RFP'S": {
    default: "icon",
    selected: "icon",
  }
};

const Item = ({ title, to, selected, setSelected, children, isOpen, setIsOpen, setItemSelected, imgStyle, isCollapsed }) => {
  const handleSubmenuClick = () => {
    setIsOpen(isOpen === title ? null : title);
  };
  const isSelected = selected === title;
  const iconMapping = iconMappings[title] || { default: '', selected: '' };
  const iconSrc = isSelected ? iconMapping.selected : iconMapping.default;
  const iconBackgroundColor = isSelected ? "transparent" : "rgba(215, 233, 245)";
  const textColor = isSelected ? "#fff" : "#000";
  const isWinningRfp = title === "Winning RFP'S";

  if (children) {
    return (
      <SubMenu
        title={!isCollapsed ? (
          <Typography sx={{ fontWeight: 500, fontSize: '14px', color: textColor }}>
            {title}
          </Typography>
        ) : null}
        icon={<img src={iconSrc} alt={title} style={{ width: 20, height: 20 }} />}
        open={isOpen === title}
        onOpenChange={handleSubmenuClick}
      >
        {children}
      </SubMenu>
    );
  }

  return (
    <MenuItem
      active={isSelected}
      style={{
        backgroundColor: isSelected ? "#0175ff" : "transparent",
        borderRadius: 10,
        color: textColor,
        fontWeight: isSelected ? "bold" : "normal",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: isCollapsed ? '50px' : '230px',
        marginLeft: isCollapsed ? '4px' : '10px'
      }}
      onClick={() => {
        setSelected(title);
        setItemSelected(true);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ backgroundColor: iconBackgroundColor, padding: 8, borderRadius: '10px', marginRight: 10 }} className="imagespan">
          {isWinningRfp ? (
            <EmojiEventsIcon sx={{ width: imgStyle.width, height: imgStyle.height, color: isSelected ? "#fff" : "#0175ff" }} />
          ) : (
            <img src={iconSrc} alt={title} style={imgStyle} className="images" />
          )}
        </span>
        {!isCollapsed && (
          <Typography sx={{ fontWeight: 500, fontSize: '14px', color: textColor }}>
            {title}
          </Typography>
        )}
      </Box>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = ({ isSidebar, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(null);
  const [itemSelected, setItemSelected] = useState(false);

  const pathToTitle = {
    "/dashboard": "Dashboard",
    "/reports": "Reports",
    "/visual-reports": "Visual Reports",
    "/data-reports": "Data Reports",
    "/content-library": "Content Library",
    "/prompts": "Prompts",
    "/winning-rfps": "Winning RFP'S",
  };
  
  const resolveTitleFromPath = (path) => {
    if (!path) return "Dashboard";
    if (path.startsWith("/section-details") || path.startsWith("/edit-section-details")) {
      return "Content Library";
    }  
    return pathToTitle[path] || "Dashboard";
  };
  const [selected, setSelected] = useState(() => resolveTitleFromPath(location.pathname));
  useEffect(() => {
    const title = resolveTitleFromPath(location.pathname);
    setSelected(title);
  }, [location.pathname]);

  const isMobileView = useMediaQuery('(max-width:600px)');

  const imgStyle = {
    width: isCollapsed ? '17px' : '20px',
    height: isCollapsed ? '17px' : '20px',
    transition: 'width 0.3s ease, height 0.3s ease',
  };

  const storedData = JSON.parse(localStorage.getItem('userDetails') || 'null');
  const userRole = storedData?.userDetails?.userRole || null;

  return (
    <Container>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: isCollapsed ? '62px' : '250px',
          backgroundColor: '#fff',
          zIndex: 1000,
          boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
          "& .pro-sidebar-inner": {
            background: `#fff !important`,
          },
          "& .pro-sidebar .pro-menu.shaped .pro-menu-item > .pro-inner-item > .pro-icon-wrapper": {
            backgroundColor: 'rgba(215, 233, 245)'
          },
          "& .pro-sidebar .pro-menu.square .pro-menu-item > .pro-inner-item > .pro-icon-wrapper": {
            borderRadius: '10px',
            marginLeft: isCollapsed ? '4px' : '10px'
          },
          "& .pro-sidebar .pro-menu .pro-menu-item.pro-sub-menu > .pro-inner-item > .pro-arrow-wrapper": {
            display: 'none'
          },
          "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item": {
            padding: isCollapsed ? '8px 34px 8px 10px' : '8px 35px 8px 20px'
          },
          "& .pro-sidebar .pro-menu .pro-menu-item.pro-sub-menu .pro-inner-list-item  ": {
            paddingLeft: isCollapsed ? "0px" : "24px"
          },
          "& .pro-sidebar .pro-menu .pro-menu-item.pro-sub-menu .pro-inner-list-item .pro-inner-item": {
            padding: isCollapsed ? "8px 30px 8px 10px" : "8px 30px 8px 15px"
          }

        }}
      >
        <ProSidebar collapsed={isCollapsed}>
          <Menu iconShape="square">
            <MenuItem style={{ color: "#0175ff" }}>
              {!isCollapsed && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  ml="15px"
                >
                  <img
                    src={MITLOGO}
                    style={{
                      height: 50,
                      width: 180,
                      position: 'relative',
                      top: '0',
                      cursor: 'auto'
                    }}
                    alt="LOGO"
                  />
                  {isMobileView && (
                    <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                      <MenuOutlinedIcon />
                    </IconButton>
                  )}
                </Box>
              )}
              {isCollapsed && (
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              )}
              <Divider sx={{ borderColor: '#0D6EFD', paddingTop: '10px' }} />
            </MenuItem>

            <Box>
              <Item
                title="Dashboard"
                to="/dashboard"
                selected={selected}
                setSelected={setSelected}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setItemSelected={setItemSelected}
                imgStyle={imgStyle}
                isCollapsed={isCollapsed}
              />
              <Item
                title="Reports"
                to='/d'
                selected={selected}
                setSelected={setSelected}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setItemSelected={setItemSelected}
                imgStyle={imgStyle}
                isCollapsed={isCollapsed}
              >
                <Item
                  title="Visual Reports"
                  to="/visual-reports"
                  selected={selected}
                  setSelected={setSelected}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  setItemSelected={setItemSelected}
                  imgStyle={imgStyle}
                  isCollapsed={isCollapsed}

                />
                <Item
                  title="Data Reports"
                  to="/data-reports"
                  selected={selected}
                  setSelected={setSelected}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  setItemSelected={setItemSelected}
                  imgStyle={imgStyle}
                  isCollapsed={isCollapsed}
                />
              </Item>
              <Item
                title="Content Library"
                to="/content-library"
                selected={selected}
                setSelected={setSelected}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setItemSelected={setItemSelected}
                imgStyle={imgStyle}
                isCollapsed={isCollapsed}
              />
              <Item
                title="Winning RFP'S"
                to="/winning-rfps"
                selected={selected}
                setSelected={setSelected}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setItemSelected={setItemSelected}
                imgStyle={imgStyle}
                isCollapsed={isCollapsed}
              />
              {userRole === "executive_person" && (
              <Item
                title="Prompts"
                to="/prompts"
                selected={selected}
                setSelected={setSelected}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setItemSelected={setItemSelected}
                imgStyle={imgStyle}
                isCollapsed={isCollapsed}
              />
              )}
            </Box>
          </Menu>
        </ProSidebar>
      </Box>
    </Container>
  );
};

export default Sidebar;